import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useAuth } from '../../hooks/useAuth'
import { usePagination } from '../../hooks/usePagination'
import { useConfirm } from '../../hooks/useConfirm'
import { catalogReviewService } from '../../services/catalogReviewService'
import catalogService from '../../services/catalogService'
import { FoodGroup } from '../../types/catalog'
import { CatalogAlias, ReviewKind, UnmatchedItem } from '../../types/catalogReview'
import { Pagination } from '../../components/ui/Pagination'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'
import { tokens } from '../../styles/tokens'
import { UnmatchedRow } from './UnmatchedRow'
import { AliasRow } from './AliasRow'
import { ReviewDialog } from './ReviewDialog'

type View = 'pending' | 'decided'

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const Tab = styled.button<{ $active: boolean }>`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid ${p => (p.$active ? tokens.purple600 : tokens.purple100)};
  background: ${p => (p.$active ? tokens.purple600 : 'white')};
  color: ${p => (p.$active ? 'white' : tokens.purple700)};
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  cursor: pointer;
`

const Spacer = styled.span`
  width: 12px;
`

const Intro = styled.p`
  font-size: 13px;
  color: ${tokens.purple400};
  margin: -8px 0 18px;
  max-width: 720px;
`

const Notice = styled.div`
  background: ${tokens.purple50};
  border: 1px solid ${tokens.purple100};
  color: ${tokens.purple700};
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 16px;
`

const KIND_LABEL: Record<ReviewKind, string> = { ingredient: 'Ingredients', unit: 'Units' }

export function CatalogReviewPage() {
  const { token } = useAuth()
  const { page, perPage, setPage } = usePagination()
  const svc = useMemo(() => catalogReviewService(token), [token])
  const confirmDialog = useConfirm()

  const [kind, setKind] = useState<ReviewKind>('ingredient')
  const [view, setView] = useState<View>('pending')
  const [pending, setPending] = useState<UnmatchedItem[]>([])
  const [aliases, setAliases] = useState<CatalogAlias[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [reviewing, setReviewing] = useState<UnmatchedItem | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [foodGroups, setFoodGroups] = useState<FoodGroup[]>([])

  useEffect(() => {
    catalogService(token)
      .foodGroups.list(1, 100)
      .then(res => setFoodGroups(res.items))
      .catch(() => setFoodGroups([]))
  }, [token])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      if (view === 'pending') {
        const res = await svc.queue(kind, page, perPage)
        setPending(res.items)
        setTotal(res.total)
      } else {
        const res = await svc.aliases(kind, page, perPage)
        setAliases(res.items)
        setTotal(res.total)
      }
    } finally {
      setLoading(false)
    }
  }, [svc, kind, view, page, perPage])

  useEffect(() => {
    load()
  }, [load])

  const switchTo = useCallback(
    (nextKind: ReviewKind, nextView: View) => {
      setKind(nextKind)
      setView(nextView)
      setPage(1)
      setNotice(null)
    },
    [setPage]
  )

  const handleDone = useCallback(
    (summary: string) => {
      setReviewing(null)
      setNotice(summary)
      load()
    },
    [load]
  )

  const closeReview = useCallback(() => setReviewing(null), [])

  const handleUndo = useCallback(
    (alias: CatalogAlias) => {
      confirmDialog.confirm(
        'Undo decision',
        `Forget the decision for "${alias.text}"? It stops auto-linking new recipes and returns to the queue if any recipe still has it as free text. Recipes already linked keep their link.`,
        async () => {
          await svc.deleteAlias(kind, alias.id)
          load()
        }
      )
    },
    [confirmDialog, svc, kind, load]
  )

  const cols = view === 'pending' ? 4 : 5

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Catalog</PageEyebrow>
          <PageTitle>Review submissions</PageTitle>
        </PageMeta>
      </PageHeader>
      <Intro>
        Ingredients and units users typed instead of picking from the catalog. Link each one to a
        catalog item, add it as a new ingredient, or dismiss it. Every decision also applies to
        future recipes, and authors are asked whether to keep their own wording.
      </Intro>

      <Tabs>
        {(['ingredient', 'unit'] as const).map(k => (
          <Tab key={k} $active={kind === k} onClick={() => switchTo(k, view)}>
            {KIND_LABEL[k]}
          </Tab>
        ))}
        <Spacer />
        <Tab $active={view === 'pending'} onClick={() => switchTo(kind, 'pending')}>
          Pending
        </Tab>
        <Tab $active={view === 'decided'} onClick={() => switchTo(kind, 'decided')}>
          Decided
        </Tab>
      </Tabs>

      {notice && <Notice>{notice}</Notice>}

      {loading ? (
        <TableSkeleton cols={cols} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                {view === 'pending' ? (
                  <tr>
                    <TH>Written as</TH>
                    <TH>Used in</TH>
                    <TH>First seen</TH>
                    <TH />
                  </tr>
                ) : (
                  <tr>
                    <TH>Written as</TH>
                    <TH>Decision</TH>
                    <TH>By</TH>
                    <TH>Date</TH>
                    <TH />
                  </tr>
                )}
              </THead>
              <tbody>
                {total === 0 && (
                  <tr>
                    <td colSpan={cols}>
                      <EmptyState
                        message={view === 'pending' ? 'Nothing to review 🎉' : 'No decisions yet.'}
                      />
                    </td>
                  </tr>
                )}
                {view === 'pending' &&
                  pending.map(item => (
                    <UnmatchedRow key={item.normalizedText} item={item} onReview={setReviewing} />
                  ))}
                {view === 'decided' &&
                  aliases.map(alias => (
                    <AliasRow key={alias.id} alias={alias} onUndo={handleUndo} />
                  ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      <ReviewDialog
        kind={kind}
        item={reviewing}
        token={token}
        foodGroups={foodGroups}
        onClose={closeReview}
        onDone={handleDone}
      />
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Undo"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )
}
